import { useState, useEffect } from "react";
import { Smartphone, X } from "lucide-react";
import InstallInstructionsContent from "./InstallInstructionsContent";

/**
 * Always-available "Install on Home Screen" button anchored to the bottom of
 * the screen. Opens a modal with the same iPhone / Android / Computer
 * instructions as the auto-popup.
 *
 * Hidden once the user has installed the PWA (display-mode: standalone).
 */
const InstallHelpButton = () => {
  const [open, setOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  // Manual user dismissal — persisted in localStorage so it stays gone across
  // sessions (iOS doesn't always set display-mode:standalone reliably, so we
  // give users a manual escape hatch: "I've already installed").
  const [userDismissed, setUserDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("installHelpDismissed") === "true"
  );

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Mobile detection — Lauren only wants this surfaced for phone users
    // (iPhone, Android, Google Pixel).
    const ua = window.navigator.userAgent.toLowerCase();
    setIsMobile(/iphone|ipad|ipod|android/.test(ua));

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    const handleInstalled = () => {
      setIsStandalone(true);
      // If the browser fires appinstalled, persist that so future cold loads
      // (where matchMedia might miss it briefly) also keep the bubble hidden.
      try { localStorage.setItem("installHelpDismissed", "true"); } catch {}
    };
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const dismissForever = (e) => {
    e.stopPropagation();
    try { localStorage.setItem("installHelpDismissed", "true"); } catch {}
    setUserDismissed(true);
  };

  // Hide on desktop, when the PWA is already installed, or when the user
  // explicitly told us they don't want the nudge anymore.
  if (isStandalone || !isMobile || userDismissed) return null;

  return (
    <>
      {/* Floating button at the bottom-right (above iOS safe-area).
         Wrapped in a small flex container so we can hang a × dismiss control
         off the main button — gives users an escape hatch for "I already
         installed it" without blocking the help link if they actually need it. */}
      <div
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1 sm:bottom-6 sm:right-6"
        style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <button
          onClick={() => setOpen(true)}
          aria-label="Install on home screen"
          data-testid="install-help-button"
          className="flex items-center gap-2 px-4 py-2.5 rounded-full
                     bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/30
                     text-sm font-medium transition-all hover:scale-105 active:scale-95"
        >
          <Smartphone className="h-4 w-4" />
          <span className="hidden sm:inline">Install on Home Screen</span>
          <span className="sm:hidden">Install</span>
        </button>
        <button
          onClick={dismissForever}
          aria-label="Hide install button — I've already installed"
          title="Hide — I've already installed it"
          data-testid="install-help-dismiss"
          className="w-7 h-7 rounded-full bg-white/95 dark:bg-slate-800 text-muted-foreground
                     hover:text-red-500 hover:bg-white shadow-md ring-1 ring-black/5
                     flex items-center justify-center transition-all hover:scale-110"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Modal */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
            onClick={() => setOpen(false)}
            data-testid="install-help-backdrop"
          />
          <div
            className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50 p-4 pb-20 sm:pb-4 pointer-events-none"
            data-testid="install-help-modal"
          >
            <div
              className="bg-background border border-primary/20 rounded-2xl shadow-2xl max-w-md mx-auto overflow-hidden w-full animate-slide-up pointer-events-auto"
            >
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 p-4 border-b border-primary/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="/logo192.png"
                      alt="Lala Land"
                      className="w-12 h-12 rounded-xl shadow-lg object-cover"
                    />
                    <div>
                      <h3 className="font-bold text-primary text-lg">Install Lala Land</h3>
                      <p className="text-xs text-muted-foreground">Pick your device below</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                    aria-label="Close"
                    data-testid="install-help-close"
                  >
                    <X className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="p-4 max-h-[70vh] overflow-y-auto">
                <InstallInstructionsContent
                  deferredPrompt={deferredPrompt}
                  onInstalled={() => setOpen(false)}
                />
              </div>

              <div className="p-4 pt-2 pb-6">
                <button
                  onClick={() => setOpen(false)}
                  className="w-full py-3 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-secondary/30 rounded-xl"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default InstallHelpButton;
